import { requestService } from '@services/request';
import { appUserStore } from '@stores/bootstrap-stores/user';

export interface Prompt {
  text: string;
}

export interface CreateChatSessionRequest {
  /** Optional session ID provided by client. If not provided, server generates one */
  sessionId?: string;
  /** Optional user ID for tracking session ownership */
  userId?: string;
  /** Optional system instruction for the chat session */
  systemInstruction?: string;
}

export interface CreateChatSessionResponse {
  sessionId: string;
}

export interface SendMessageResponse {
  response: unknown;
  sessionId: string;
  timestamp: number;
}

export class VertexService {
  private readonly basePath = '/admin/ai';

  /**
   * Generate unique session ID (client-side)
   * Format: timestamp-randomstring
   */
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }

  /**
   * Generate AI Content (Legacy endpoint)
   *
   * Generates AI content based on the provided prompts.
   * @param prompts - Array of prompt strings
   * @param systemInstruction - Optional system instruction for the AI model
   * @returns Observable with AI response
   */
  generate(
    prompts: string[],
    systemInstruction?: string
  ): Promise<{
    result: { [key: string]: unknown[] }[];
    dataType: 'object' | 'array' | 'string';
  }> {
    return requestService<{
      result: { [key: string]: unknown[] }[];
      dataType: 'object' | 'array' | 'string';
    }>(`${this.basePath}/generate`, {
      method: 'POST',
      body: {
        parts: prompts.map((prompt) => ({
          text: prompt
        })),
        ...(systemInstruction && { system_instruction: systemInstruction })
      }
    });
  }

  /**
   * Machine-translates an email subject and HTML body. Merge tags are preserved by the backend,
   * which rejects translations that alter them.
   */
  translateEmail(
    text: { subject: string; body: string },
    targetLanguage: string
  ): Promise<{ subject: string; body: string }> {
    return requestService<{ subject: string; body: string }>(`${this.basePath}/translate-email`, {
      method: 'POST',
      body: { ...text, target_language: targetLanguage }
    });
  }

  /** Machine-translates Survey Creator strings; the result keeps the input order. */
  translateStrings(strings: string[], toLocale: string, fromLocale?: string): Promise<string[]> {
    return requestService<string[]>(`${this.basePath}/translate-strings`, {
      method: 'POST',
      body: { strings, to_locale: toLocale, from_locale: fromLocale }
    });
  }

  /**
   * Create a new chat session
   *
   * Initializes a new stateful chat session with the Vertex AI model.
   * Generates a unique session ID on the client side and sends it to the server.
   * @param systemInstruction - Optional system instruction for the chat session
   * @returns Observable with session ID
   */
  createChatSession(systemInstruction?: string): Promise<CreateChatSessionResponse> {
    return requestService<CreateChatSessionResponse>(`${this.basePath}/chat`, {
      method: 'POST',
      body: {
        sessionId: this.generateSessionId(),
        ...(systemInstruction && { systemInstruction }),
        userId: appUserStore.getState().appUser?.id
      }
    });
  }

  /**
   * Send a message to a chat session
   *
   * Sends a message to an existing chat session and returns the model's response.
   * @param sessionId - The chat session ID
   * @param prompts - Array of prompt strings to send
   * @returns Observable with AI response
   */
  sendChatMessage(sessionId: string, parts: Prompt[]): Promise<SendMessageResponse> {
    return requestService<SendMessageResponse>(`${this.basePath}/chat/${sessionId}/message`, {
      method: 'POST',
      body: {
        parts
      }
    });
  }

  /**
   * End a chat session
   *
   * Terminates a chat session and removes it from memory.
   * @param sessionId - The chat session ID to end
   * @returns Observable with success status
   */
  endChatSession(sessionId: string): Promise<{ success: boolean }> {
    return requestService<{ success: boolean }>(`${this.basePath}/chat/${sessionId}`, {
      method: 'DELETE'
    });
  }
}

export const vertexService = new VertexService();
